import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { Competitions } from './LivescoreContext';
import { useSet } from '@/hooks/useSet';
import { setOwnFavoriteCompetitors, setOwnFavoriteMatches } from '@/api/user';
import { useToast } from '@/hooks/useToast';
import { useAuthCookies } from '@/hooks/useAuthCookies';
import { ApiTeamsGet200ResponseDataTeamsInner } from '@/api/livescore/generated';
import { livescoreClient } from '@/api/livescore/client';
import { convertCountryName } from '@/utils/countryCode';

interface LivescoreLayoutContextType {
  competitions: Competitions;
  favoriteMatches: Set<string>;
  favoriteCompetitors: Set<string>;
  toggleMatchToFavorites: (matchId: string) => Promise<void>;
  toggleFavoriteCompetitors: (competitorId: string) => Promise<void>;
  teams: Team[];
  teamsLoading: boolean;
  favoriteCompetitorsModalOpen: boolean;
  setFavoriteCompetitorsModalOpen: Dispatch<SetStateAction<boolean>>;
}

const LivescoreLayoutContext = createContext<LivescoreLayoutContextType | undefined>(undefined);

interface LivescoreLayoutProviderProps {
  children: ReactNode;
  competitions: Competitions;
  favoriteMatchesInit: string[];
  favoriteCompetitorsInit: string[];
}

export type Team = ApiTeamsGet200ResponseDataTeamsInner & {
  logo_url?: string;
};

export const LivescoreLayoutProvider: React.FC<LivescoreLayoutProviderProps> = ({
  children,
  competitions,
  favoriteMatchesInit,
  favoriteCompetitorsInit,
}) => {
  const favoriteMatches = useSet<string>(favoriteMatchesInit);
  const favoriteCompetitors = useSet<string>(favoriteCompetitorsInit);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [favoriteCompetitorsModalOpen, setFavoriteCompetitorsModalOpen] = useState(false);

  const {
    showErrorToggleFavoriteCompetitorToast,
    showErrorToggleFavoriteMatchToast,
    showErrorToggleFavoriteMatchNotLoggedInToast,
    showLivescoreFetchTeamsErrorToast,
  } = useToast();

  useEffect(() => {
    async function fetchTeamsData() {
      const response = await livescoreClient.apiTeamsGet();

      if (!response.data.success) {
        showLivescoreFetchTeamsErrorToast();
      }

      setTeams(response.data.data.teams);
      setTeamsLoading(false);
    }

    fetchTeamsData();
  }, []);

  const { token } = useAuthCookies();

  const toggleMatchToFavorites = async (matchId: string) => {
    if (!token) {
      showErrorToggleFavoriteMatchNotLoggedInToast();
      return;
    }

    let newFavoriteMatches;

    if (!favoriteMatches.has(matchId)) {
      newFavoriteMatches = [...favoriteMatches.values, matchId];
    } else {
      newFavoriteMatches = [...favoriteMatches.values].filter((id) => id !== matchId);
    }

    const response = await setOwnFavoriteMatches(token, newFavoriteMatches);
    const status = response.status.toString();

    if (!status.startsWith('2')) {
      showErrorToggleFavoriteMatchToast(favoriteMatches.has(matchId));
      return;
    }

    favoriteMatches.clear();
    newFavoriteMatches.forEach((val) => {
      favoriteMatches.add(val);
    });

    return;
  };

  const toggleFavoriteCompetitors = async (competitorId: string) => {
    const oldFavoriteCompetitors = Array.from(favoriteCompetitors.values);
    let newFavoriteCompetitors;

    if (favoriteCompetitors.has(competitorId)) {
      newFavoriteCompetitors = oldFavoriteCompetitors.filter((comp) => comp !== competitorId);
    } else {
      newFavoriteCompetitors = [...oldFavoriteCompetitors, competitorId];
    }

    const response = await setOwnFavoriteCompetitors(token, newFavoriteCompetitors);

    if (!response.status.toString().startsWith('2')) {
      showErrorToggleFavoriteCompetitorToast(favoriteCompetitors.has(competitorId));
      return;
    }

    favoriteCompetitors.clear();
    newFavoriteCompetitors.forEach((val) => {
      favoriteCompetitors.add(val);
    });
  };

  return (
    <LivescoreLayoutContext.Provider
      value={{
        competitions,
        favoriteMatches: favoriteMatches.values,
        favoriteCompetitors: favoriteCompetitors.values,
        toggleMatchToFavorites,
        toggleFavoriteCompetitors,
        teamsLoading,
        teams,
        favoriteCompetitorsModalOpen,
        setFavoriteCompetitorsModalOpen,
      }}
    >
      {children}
    </LivescoreLayoutContext.Provider>
  );
};

export const useLivescoreLayout = () => {
  const context = useContext(LivescoreLayoutContext);
  if (!context) {
    throw new Error('useLivescoreLayout must be used within an LivescoreLayoutProvider');
  }
  return context;
};
